import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DndContext, DragOverlay, MouseSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { Mail, Phone, Plus, RefreshCw, Search } from "lucide-react";
import PageHeader, { ghostBtn } from "@/app/PageHeader";
import { useSession } from "@/app/session";

// Same stages as the CRM (backend/crm_logic.py STATUSES).
const STAGES = [
  { key: "new", label: "New", tone: "border-t-slate-400" },
  { key: "contacted", label: "Contacted", tone: "border-t-sky-400" },
  { key: "meeting", label: "Meeting", tone: "border-t-vortex-cyan" },
  { key: "proposal", label: "Proposal", tone: "border-t-amber-300" },
  { key: "won", label: "Won", tone: "border-t-emerald-400" },
  { key: "lost", label: "Lost", tone: "border-t-red-400" },
];

const PRIORITY = { A: "text-emerald-300", B: "text-amber-300", C: "text-slate-500" };

const LeadCard = ({ lead, overlay = false }) => {
  const step = lead.next_steps?.[0];
  return (
    <div className={`rounded-md border border-white/10 bg-vortex-ink p-3 text-left ${overlay ? "shadow-2xl ring-1 ring-vortex-cyan/60" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 truncate text-sm font-medium text-slate-100">{lead.company}</p>
        <span className="shrink-0 font-mono text-[10px] text-slate-500">{lead.score}</span>
      </div>
      {(lead.contact_name || lead.city) && (
        <p className="mt-0.5 truncate text-xs text-slate-500">{[lead.contact_name, lead.city].filter(Boolean).join(" · ")}</p>
      )}
      {step && (
        <p className={`mt-2 truncate text-xs ${step.urgency === "high" ? "text-vortex-cyan" : "text-slate-400"}`}>{step.title}</p>
      )}
      <div className="mt-2 flex items-center gap-2 text-slate-500">
        {lead.phone && <Phone size={12} />}
        {lead.email && <Mail size={12} />}
        {lead.priority && (
          <span className={`ml-auto font-mono text-[9px] tracking-[0.15em] uppercase ${PRIORITY[lead.priority] || "text-slate-500"}`}>Priority {lead.priority}</span>
        )}
      </div>
    </div>
  );
};

const DraggableLead = ({ lead, onOpen }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: lead.id, data: { lead } });
  return (
    <button ref={setNodeRef} {...attributes} {...listeners} onClick={() => onOpen(lead)}
      className={`block w-full touch-manipulation ${isDragging ? "opacity-30" : ""}`} data-testid={`pipeline-card-${lead.id}`}>
      <LeadCard lead={lead} />
    </button>
  );
};

const Column = ({ stage, leads, onOpen }) => {
  const { setNodeRef, isOver } = useDroppable({ id: stage.key });
  return (
    <div ref={setNodeRef} data-testid={`pipeline-col-${stage.key}`}
      className={`flex min-w-[15rem] flex-1 flex-col rounded-md border border-t-2 border-white/10 ${stage.tone} ${isOver ? "bg-vortex-cyan/5" : "bg-vortex-surface/60"}`}>
      <div className="flex items-center justify-between px-3 py-3">
        <p className="font-mono text-[10px] tracking-[0.2em] text-slate-300 uppercase">{stage.label}</p>
        <span className="rounded-full bg-white/5 px-2 py-0.5 font-mono text-[10px] text-slate-400">{leads.length}</span>
      </div>
      <div className="flex max-h-[65vh] min-h-[120px] flex-col gap-2 overflow-y-auto px-2 pb-3" data-lenis-prevent>
        {leads.map((l) => <DraggableLead key={l.id} lead={l} onOpen={onOpen} />)}
      </div>
    </div>
  );
};

// Kanban view of every lead. Drag a card to change its stage; click it to open the lead.
const Pipeline = () => {
  const { api, onAuthError } = useSession();
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");
  const [showLost, setShowLost] = useState(false);
  const [dragging, setDragging] = useState(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
  );

  const load = useCallback(() => {
    api.get("/crm/leads").then((r) => setLeads(r.data)).catch((e) => onAuthError(e, "Could not load leads."));
  }, [api, onAuthError]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (leads || []).filter((l) => !term
      || [l.company, l.contact_name, l.city, l.email].some((v) => (v || "").toLowerCase().includes(term)));
  }, [leads, q]);

  const stages = showLost ? STAGES : STAGES.filter((s) => s.key !== "lost");

  const onDragEnd = async ({ active, over }) => {
    setDragging(null);
    const lead = active.data.current?.lead;
    if (!lead || !over || over.id === lead.status) return;
    const status = over.id;
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    try {
      const res = await api.patch(`/crm/leads/${lead.id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? res.data : l)));
      toast.success(`${lead.company} moved to ${STAGES.find((s) => s.key === status).label}.`);
    } catch (e) {
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? lead : l)));
      onAuthError(e, "Could not move the lead.");
    }
  };

  return (
    <div data-testid="pipeline-page">
      <PageHeader title="Pipeline"
        intro="Every lead by stage. Drag a card to move it along; click it to open the lead, log a call or add notes."
        actions={<>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter cards" aria-label="Filter cards"
              className="h-9 w-44 rounded-full border border-white/15 bg-vortex-ink pl-8 pr-3 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-vortex-cyan" />
          </div>
          <button onClick={() => setShowLost((v) => !v)} className={ghostBtn}>{showLost ? "Hide lost" : "Show lost"}</button>
          <button onClick={load} className={ghostBtn}><RefreshCw size={14} /> Refresh</button>
          <Link to="/admin/crm" className={ghostBtn}><Plus size={14} /> Find leads</Link>
        </>} />

      {leads === null ? (
        <p className="text-sm text-slate-500">Loading pipeline...</p>
      ) : (
        <DndContext sensors={sensors} onDragStart={({ active }) => setDragging(active.data.current?.lead)}
          onDragCancel={() => setDragging(null)} onDragEnd={onDragEnd}>
          <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8" data-lenis-prevent>
            {stages.map((s) => (
              <Column key={s.key} stage={s} leads={filtered.filter((l) => (l.status || "new") === s.key)}
                onOpen={(l) => navigate(`/admin/crm?lead=${l.id}`)} />
            ))}
          </div>
          <DragOverlay>{dragging ? <div className="w-64"><LeadCard lead={dragging} overlay /></div> : null}</DragOverlay>
        </DndContext>
      )}
    </div>
  );
};

export default Pipeline;
